// Pure flight-tracking helpers (airport identity + calendar dates), split out of
// ./helpers.js so they can be imported without dragging in `connectedApiService`
// (helpers.js imports ../../index, which boots the whole app as a side effect).
// helpers.js re-exports everything here, so existing consumers are unaffected and
// there is still a single implementation of each.
import moment from 'moment'

// The calendar date a timestamp falls on IN ITS OWN timezone. `parseZone` keeps the
// offset carried by FlightNerve's ISO strings (…T11:13:17-04:00 -> 2026-07-28) rather
// than shifting to UTC, and passes naive 'YYYY-MM-DD HH:mm' strings through as-is.
// Comparing these beats `new Date(x).getUTCDate()`, which has two bugs: it mis-dates
// every late-evening arrival (23:30 EDT is already "tomorrow" in UTC), and comparing
// day-of-month alone treats 30 Jun and 30 Jul as the same day.
export function localCalendarDate(value) {
  if (!value) return null
  const parsed = moment.parseZone(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

// ── The local day of a leg, when there is no ISO to read it from ────────────────
// Both providers publish a display string in "HH:MM, Mon DD" form, and it is the LOCAL wall
// clock at that airport for both — verified on paired calls for QR1002, EK206 and SQ317.
// It carries no YEAR, so one is inferred from the day the booking is against, with the
// only ambiguous case handled explicitly: a January reading against a December booking is
// next year, and a December reading against a January booking is last year.
//
// Needed because the ISO field is not always present. flightapi.io returned QR1002's
// arrival as scheduledTime "03:25, Aug 25" with arrivalDateTime null, and without this the
// leg would have no resolvable day at all.
//
// Accepts both spellings seen live: "Jan 01" (flightapi.io) and "Jan 1" (FlightNerve).
const SCHEDULED_TIME_MONTHS = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
}

export function localDayFromScheduledTime(scheduledTime, referenceDay) {
  if (typeof scheduledTime !== 'string' || !referenceDay) return null
  const parts = scheduledTime.match(/,\s*([A-Za-z]{3})\s+(\d{1,2})\s*$/)
  if (!parts) return null
  const month = SCHEDULED_TIME_MONTHS[parts[1].toUpperCase()]
  if (month === undefined) return null
  const day = parseInt(parts[2], 10)
  const reference = moment.parseZone(referenceDay)
  if (!reference.isValid()) return null

  let year = reference.year()
  if (month === 11 && reference.month() === 0) year -= 1
  else if (month === 0 && reference.month() === 11) year += 1

  const built = moment.utc({ year, month, day })
  // Rejects an impossible date (Feb 30) rather than letting moment roll it forward.
  return built.isValid() && built.date() === day
    ? built.format('YYYY-MM-DD')
    : null
}

// The calendar day a leg happens on, at its own airport.
//
// The ISO field is preferred and read with localCalendarDate, which keeps whatever offset
// the provider attached instead of shifting to UTC. That distinction is the whole point:
// flightapi.io labels a local wall clock with a ZERO offset (EK206 arrives Dubai
// "23:10, Aug 26" as 2026-08-26T23:10:00-00:00), so converting it into Asia/Dubai moves it
// to 03:10 on the 27th and reports the wrong day. Reading the offset as given returns the
// 26th, which is what the airport clock says — and the same reading is correct for
// FlightNerve, whose offsets are real (…T03:25:00+04:00 -> the 25th).
export function legLocalDay(localIso, scheduledTime, referenceDay) {
  const fromIso = localCalendarDate(localIso)
  if (fromIso) return fromIso
  return localDayFromScheduledTime(scheduledTime, referenceDay)
}

// Does this string even look like a flight number?
//
// The search fires while the user is still typing, so "BA830" also arrives as B, BA, BA8,
// BA83. Each of those reached the "no flight found" branch, and once that branch started
// logging, every keystroke became a #flight-tracking-errors entry ("BA", "EK", "A",
// "9185", "null" …). Callers only call out and only log when this returns true.
//
// True means the value matches the RD-4073 format rule (see isValidFlightNumberFormat
// below): a 3-letter ICAO or 2-character IATA code followed by 1-4 digits. The ticket
// accepts an all-numeric designator ("123456"), so this no longer requires a letter; such a
// value reaches the API and comes back as "Flight not verified" (flightapi.io 400s it).
//
// LIMIT, deliberately: this cannot tell a prefix from a short flight number. "BA8" and
// "BA83" pass, because "UA1", "AA1" and "DL1" are real flights and a MINIMUM length rule
// would reject them. So it removes the no-digit prefixes ("B", "BA", "EK") — the bulk of
// the keystroke traffic. Suppressing the remaining short prefixes is the search trigger's
// job (debounce), not this predicate's; do not add a minimum-digit rule here.
//
// The MAXIMUM is different, and is enforced: IATA allows at most four digits, so five or
// more is not a flight number under any airline — it is a typo mid-edit, or a stale client
// sending junk. Our own API already refuses these (`isPlausibleFlightQuery`,
// /^\d{1,4}$/ in flightNerve.js) and answers 400 incomplete_flight_number, so every one of
// them was a round trip that could not possibly succeed, plus a Slack entry nobody can act
// on. Measured in the 4-9 Aug channel: LA002464 cost EIGHT calls
// (route x2 + status x6), DL21020 three, EC56454 four, U2 10163 four. Matching the server
// rule here stops all of them at the keyboard — no request, and no log.
const MAX_FLIGHT_NUMBER_DIGITS = 4

// RD-4073 format rule, shared by the field ("Invalid flight number format"), the network
// gate and the Slack-report gate:
//   IATA  ^[A-Z0-9]{2}\d{1,4}$     AB1234, A12, 9W1234, 1A23, 123456
//   ICAO  ^[A-Z]{3}\d{1,4}$        ABC123, EZY8538, XYZ1234
// Spaces are stripped first; ANY other non-alphanumeric ("-", "#", "/") is rejected rather
// than stripped — the old predicate silently dropped "-", so "AA-123" went to the API.
//
// One deliberate deviation from the ticket's literal regex: leading zeros in the digit run
// are stripped BEFORE the 4-digit cap. Tickets print "LA02464" for LA2464, parseFlightNumber
// already strips the padding before the API sees it, and flightapi.io answers the padded
// form identically (live probe 2026-10-05: BA0117 == BA117). Counting the raw run rejected
// correctly-padded real flights before any lookup. Post-strip: LA02464 -> 2464 (allowed),
// DL21020 -> 21020 (blocked).
//
// The ticket's 3-7 character bound needs no separate check: the designator is 2-3 chars
// and the normalised digit run 1-4.
export function isValidFlightNumberFormat(input) {
  const compact = String(input == null ? '' : input)
    .toUpperCase()
    .replace(/\s+/g, '')
  if (!compact || !/^[A-Z0-9]+$/.test(compact)) return false
  const parts =
    compact.match(/^([A-Z]{3})(\d+)$/) || compact.match(/^([A-Z0-9]{2})(\d+)$/)
  if (!parts) return false
  const digits = parts[2].replace(/^0+(?=\d)/, '')
  return digits.length > 0 && digits.length <= MAX_FLIGHT_NUMBER_DIGITS
}

// Kept as the name the existing gates call. It used to be its own, looser rule (it required
// a letter in the designator and let "-" through); one rule now governs every gate so the
// field, the network and Slack can never disagree about what a flight number is.
export const isCompleteFlightNumber = isValidFlightNumberFormat

export const ICAO_TO_IATA = {
  // Middle East
  UAE: 'EK', // Emirates
  ETD: 'EY', // Etihad
  ABY: 'G9', // Air Arabia
  QTR: 'QR', // Qatar Airways
  MEA: 'ME', // Middle East Airlines
  GFA: 'GF', // Gulf Air
  SVA: 'SV', // Saudia
  RJA: 'RJ', // Royal Jordanian
  OMA: 'WY', // Oman Air
  KAC: 'KU', // Kuwait Airways
  FDB: 'FZ', // flydubai

  // Europe
  BAW: 'BA', // British Airways
  EZY: 'U2', // easyJet
  // easyJet Europe (Vienna AOC). EC is FlightNerve's own normalization of EJU
  // (/route?airline=EJU -> {"airline":"EC","airlineName":"easyJet Europe"}), so it is the
  // correct designator — but /airline indexes EC only sparsely. The parent-designator
  // fallback in flightNerveResolver.js covers the gap; see PARENT_AIRLINE_ALIAS there for
  // why we do NOT map straight to U2.
  EJU: 'EC',
  AFR: 'AF', // Air France
  DLH: 'LH', // Lufthansa
  SWR: 'LX', // Swiss
  KLM: 'KL', // KLM
  RYR: 'FR', // Ryanair
  THY: 'TK', // Turkish Airlines
  SAS: 'SK', // Scandinavian Airlines
  AUA: 'OS', // Austrian Airlines
  TAP: 'TP', // TAP Portugal
  IBE: 'IB', // Iberia
  AZA: 'AZ', // Alitalia / ITA Airways
  FIN: 'AY', // Finnair
  BER: 'AB', // Air Berlin
  VIR: 'VS', // Virgin Atlantic
  TOM: 'BY', // TUI Airways
  EZS: 'DS', // easyJet Switzerland
  BEL: 'SN', // Brussels Airlines
  EWG: 'EW', // Eurowings
  VLG: 'VY', // Vueling
  WZZ: 'W6', // Wizz Air
  NOZ: 'DY', // Norwegian
  NBT: 'N0', // Norse Atlantic Airways
  ICE: 'FI', // Icelandair
  EIN: 'EI', // Aer Lingus
  LOT: 'LO', // LOT Polish
  BTI: 'BT', // airBaltic
  CSA: 'OK', // Czech Airlines
  ROT: 'RO', // TAROM
  AEE: 'A3', // Aegean Airlines

  // North America
  AAL: 'AA', // American Airlines
  DAL: 'DL', // Delta
  UAL: 'UA', // United Airlines
  SWA: 'WN', // Southwest
  AWE: 'US', // US Airways
  JBU: 'B6', // JetBlue
  ACA: 'AC', // Air Canada
  WJA: 'WS', // WestJet
  ASA: 'AS', // Alaska Airlines
  AAY: 'G4', // Allegiant Air
  NKS: 'NK', // Spirit Airlines
  FFT: 'F9', // Frontier Airlines

  // Asia
  SIA: 'SQ', // Singapore Airlines
  CPA: 'CX', // Cathay Pacific
  ANA: 'NH', // All Nippon Airways
  JAL: 'JL', // Japan Airlines
  KAL: 'KE', // Korean Air
  AAR: 'OZ', // Asiana Airlines
  THA: 'TG', // Thai Airways
  MAS: 'MH', // Malaysia Airlines
  GIA: 'GA', // Garuda Indonesia
  VNA: 'VN', // Vietnam Airlines
  PAL: 'PR', // Philippine Airlines
  CCA: 'CA', // Air China
  CES: 'MU', // China Eastern
  CSN: 'CZ', // China Southern
  EVA: 'BR', // EVA Air
  CAL: 'CI', // China Airlines
  AXM: 'AK', // AirAsia
  XAX: 'D7', // AirAsia X
  AIC: 'AI', // Air India
  AXB: 'IX', // Air India Express
  HVN: 'VN', // Vietnam Airlines

  // Africa
  SAA: 'SA', // South African Airways
  ETH: 'ET', // Ethiopian Airlines
  RAM: 'AT', // Royal Air Maroc
  KQA: 'KQ', // Kenya Airways
  MSR: 'MS', // EgyptAir
  NMB: 'SW', // Air Namibia
  EXY: 'EC', // South African Express

  // Oceania
  QFA: 'QF', // Qantas
  ANZ: 'NZ', // Air New Zealand
  VOZ: 'VA', // Virgin Australia
  FJI: 'FJ', // Fiji Airways

  // South America
  LAN: 'LA', // LATAM
  TAM: 'JJ', // LATAM Brasil
  AVA: 'AV', // Avianca
  ARG: 'AR', // Aerolineas Argentinas
  GLO: 'G3', // Gol
  AMX: 'AM', // Aeromexico
  CMP: 'CM', // Copa Airlines
}

// ── One brain for flight identity ───────────────────────────────────────────
// MOVED here from FlightTracking.jsx so the lookup latch, the Slack de-duper and the
// stale-payload guards all share ONE implementation. The invariant that comment states —
// "two normalization implementations WILL eventually disagree on some input" — is the
// whole reason this lives in the pure module now: the reporter cannot import a React
// component, so keeping it there guaranteed a second copy would be written.
export function parseFlightNumber(input) {
  if (!input || typeof input !== 'string') {
    return { fname: '', fnumber: '', fOrigName: '' }
  }

  let value = input.trim().toUpperCase()
  const icaoMatch = value.match(/^([A-Z]{3})/)
  const fOrigName = icaoMatch ? icaoMatch[1] : ''

  value = value.replace(/^([A-Z]{3})/, match => ICAO_TO_IATA[match] || match)

  // Take the airline code WHOLE. This used to be `value.slice(0, 2)`, which silently
  // truncated any 3-letter ICAO code missing from the 93-entry ICAO_TO_IATA table and
  // turned it into a DIFFERENT REAL AIRLINE:
  //   ENT5156 (Enter Air)  -> "EN" = Air Dolomiti  -> 400
  //   TVP7367 (Smartwings) -> "TV"                 -> 400
  //   CAI4134 (Corendon)   -> "CA" = Air China     -> 200 with DAX->CAN, the wrong flight
  // FlightNerve's `name` takes IATA *or* ICAO (verified live: BAW/DLH/AFR/KLM/CAI all
  // resolve), so an unmapped 3-letter code is passed through untouched — CAI4134 then
  // returns Corendon AYT->STN, identical to XC4134. A code we don't recognise is never
  // worth guessing at.
  // Only treat a leading 3-letter run as the airline when digits actually follow it,
  // so 2-char codes carrying a digit (B6, 4Y, 3Z) still split correctly.
  const compact = value.replace(/\s+/g, '')
  const icaoStyle = compact.match(/^([A-Z]{3})(\d.*)$/)

  const name = icaoStyle ? icaoStyle[1] : compact.slice(0, 2)
  // Strip zero-padding too ("LA02464" -> 2464): tickets print padded numbers,
  // FlightNerve keys by the bare one, and a 5-digit padded num fails the
  // backend's 4-digit plausibility guard as incomplete_flight_number.
  const num = (icaoStyle ? icaoStyle[2] : compact.slice(2))
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')

  return { fname: name, fnumber: num, fOrigName }
}

// The canonical identity of a typed flight number: "SK835", "SK 835", "SK0835", "sk835"
// and "SAS835" are all SK835. Agents type several of these while working one booking, so
// anything keyed on the raw string treats them as different flights — a separate Slack
// entry each, and a separate paid lookup chain each.
//
// Built on parseFlightNumber, so it inherits the ICAO->IATA mapping for free: EZY213 and
// U2 213 collapse to the same key, which a hand-rolled regex could not do.
//
// Falls back to the compacted input when it does not parse as designator + number, so a
// half-typed value still gets a stable key of its own and can never collide with a real
// flight.
//
// EXCEPT for the ambiguous codes below. Two ICAO codes in the table share one IATA value
// (EJU and EXY both -> EC; VNA and HVN both -> VN), so keying on the mapped code would put
// two DIFFERENT airlines on one key. Consumers act on that: the failed-lookup latch would
// suppress an EXY5454 lookup because EJU5454 had just failed, reporting "not found" for a
// flight never queried, and the Slack de-duper would swallow the entry. This is the same
// over-reach flightNerveResolver.js documents for the parent-alias table ("TWO ICAO codes
// map to EC"). For those codes only, key on what the user actually typed.
const AMBIGUOUS_IATA_MAPPINGS = Object.values(ICAO_TO_IATA).reduce(
  (acc, iata) => {
    acc[iata] = (acc[iata] || 0) + 1
    return acc
  },
  {}
)

export function canonicalFlightKey(value) {
  const text = String(value == null ? '' : value)
  const { fname, fnumber, fOrigName } = parseFlightNumber(text)
  if (!fname || !fnumber) {
    return text.toUpperCase().replace(/[^A-Z0-9]/g, '')
  }
  const mapped = fOrigName && ICAO_TO_IATA[fOrigName]
  const designator =
    mapped && AMBIGUOUS_IATA_MAPPINGS[mapped] > 1 ? fOrigName : fname
  return `${designator}${fnumber}`
}

//Intl ↔ International for airport name / address comparison
export function normalizeIntlForAirportMatch(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\bintl\b\.?/gi, 'international')
    .replace(/\s+/g, ' ')
    .trim()
}

// ── Canonical airport ↔ flight matcher ──────────────────────────────────────
// THE single implementation of "is this flight's airport the one that was booked?".
// There used to be four, with three different answers, so the same flight could pass
// the standalone-VIP guard and fail the transfer/add-on one (and vice versa):
//   - flightNervePure / FlightTracking-legacy: case-SENSITIVE `includes` on the IATA, no
//     word boundary, and NO city fallback.
//   - vipMeetGreet/flightValidation: strict IATA equality, case-insensitive \bIATA\b,
//     name + city.
//   - StandaloneVipDetails: as above, plus COUNTRY.
// Verified disagreements on 4 of 6 realistic inputs (city-only address, lowercase IATA in
// the address, flight with a null airportCode, and an IATA embedded in another word).
// Everything now delegates here; the semantics below are the best of the three:
//   1. booked IATA known and valid -> strict equality. Unambiguous, no text guessing.
//   2. else \bIATA\b in the address, case-INSENSITIVE (a lowercased address is common).
//   3. else the flight's airport NAME or CITY as a normalised substring. City matters:
//      "Palma de Mallorca, Spain" carries no IATA and not the full airport name.
//   4. COUNTRY is deliberately NOT a candidate — AUH and DXB are both "UAE", so it
//      matches airports that are hundreds of km from the booked one.
// The IATA text match uses a word boundary so "PMIX Industrial Estate" no longer counts
// as a match for PMI.
const IATA_PATTERN = /^[A-Z]{3}$/

// ── One airport, several IATA codes ─────────────────────────────────────────
// EuroAirport Basel-Mulhouse-Freiburg is a single airport — one runway, one terminal —
// with THREE codes: BSL (Swiss), MLH (French) and EAP (the neutral EuroAirport code). It
// is the world's only binational airport, with the border running through the terminal,
// which is why it was assigned all three. A booking made against EAP therefore never
// equalled the BSL or MLH a flight API reports, so EVERY Basel booking failed the airport
// check forever, whatever the flight was. Observed live on easyJet U2 1016 at EAP.
//
// Deliberately EQUIVALENCE, not city grouping. These are the same physical airport, so the
// relation is symmetric and safe both ways. Metropolitan codes (LON, PAR, NYC, MIL, ROM)
// are a DIFFERENT relation — one code standing for several distinct airports — and are NOT
// listed here: treating them as equal would let a Gatwick flight satisfy a Heathrow
// booking and send a chauffeur to the wrong airport. A false mismatch is recoverable; a
// false match puts a car at the wrong terminal.
//
// The second group is a different shape of the same problem: a REPLACED airport whose
// old code still lives in the place database. Doha International (DIA/OTBD) closed to
// scheduled traffic in 2014 and Hamad International (DOH/OTHH) took over every commercial
// flight, but Google still returns a place named "Doha International Airport" carrying
// DIA at Hamad's own street address — so the airport picker stores DIA while FlightNerve,
// correctly, reports DOH. Verified live: /route says QR1002 is DOH -> DXB and /airline
// 2026-08-13 returns DOH -> DXB, yet every Doha booking made through that place was
// rejected as a route mismatch ("booked DIA").
//
// Safe by the same test as EuroAirport: no scheduled flight departs DIA, so equating the
// two can never put a car at an airport a passenger is actually using. It is the one-way
// case made symmetric, which is fine — a booking at DOH will never meet a flight at DIA.
const EQUIVALENT_AIRPORT_CODE_GROUPS = [
  ['BSL', 'MLH', 'EAP'],
  ['DOH', 'DIA'],
]

const AIRPORT_CODE_EQUIVALENTS = EQUIVALENT_AIRPORT_CODE_GROUPS.reduce(
  (acc, group) => {
    group.forEach(code => {
      acc[code] = group
    })
    return acc
  },
  {}
)

// Every code that refers to the same airport as `code`, itself included.
export function equivalentAirportCodes(code) {
  const upper = String(code || '')
    .trim()
    .toUpperCase()
  if (!IATA_PATTERN.test(upper)) return []
  return AIRPORT_CODE_EQUIVALENTS[upper] || [upper]
}

export function isSameAirportCode(a, b) {
  if (!a || !b) return false
  if (a === b) return true
  return equivalentAirportCodes(a).indexOf(b) !== -1
}

const bookedIataOf = airport => {
  const raw =
    airport && typeof airport === 'object' ? airport.iata || '' : airport || ''
  const code = String(raw)
    .trim()
    .toUpperCase()
  return IATA_PATTERN.test(code) ? code : null
}

const bookedAddressOf = airport => {
  if (airport && typeof airport === 'object') return airport.fullAddress || ''
  return typeof airport === 'string' ? airport : ''
}

// `airport` may be an object ({ iata, fullAddress }) or a bare address/IATA string —
// callers hold it in both shapes. `flightAirportInfo` carries the flight's own airport
// as { iata_faa | iata, name, city }.
//
// permissiveWhenFlightIataUnknown: what to do when the FLIGHT has no IATA at all (a live
// in-air instance whose airportCode is still null). Guard callers pass true — there is
// nothing to contradict the booking, so don't block it. Leg-SELECTION callers (picking
// which stop/instance a response refers to) must pass false: a blanket true there would
// make every candidate "match" and hand back an arbitrary leg.
export function airportMatchesFlight(
  airport,
  flightIata,
  flightAirportInfo,
  { permissiveWhenFlightIataUnknown = true } = {}
) {
  const code = flightIata
    ? String(flightIata)
        .trim()
        .toUpperCase()
    : null
  if (!code && permissiveWhenFlightIataUnknown) return true

  const bookedIata = bookedIataOf(airport)
  // Equivalence, not equality — see EQUIVALENT_AIRPORT_CODE_GROUPS: a booking at EAP and
  // a flight reported at BSL are the same airport.
  if (code && bookedIata) return isSameAirportCode(bookedIata, code)

  const address = bookedAddressOf(airport)
  if (!address) return false

  // Same equivalence when reading the code out of the address text: the booked address
  // says "(EAP)" while the flight reports BSL.
  if (
    code &&
    equivalentAirportCodes(code).some(candidate =>
      new RegExp(`\\b${candidate}\\b`, 'i').test(address)
    )
  )
    return true

  const normalisedAddress = normalizeIntlForAirportMatch(address)
  const candidates = [
    flightAirportInfo && flightAirportInfo.name,
    flightAirportInfo && flightAirportInfo.city,
  ].filter(Boolean)

  return candidates.some(candidate => {
    const needle = normalizeIntlForAirportMatch(candidate)
    return Boolean(needle) && normalisedAddress.includes(needle)
  })
}

// Address-shaped signature kept for the many existing call sites. Non-permissive by
// design: several of them (flightNerveResolver's route-chain walk, FlightTracking's
// "which of these results is my flight" filters) are leg selection, not a guard.
export function addressMatchesFlightAirportInfo(address, airportInfo) {
  if (!address || !airportInfo) return false
  const flightIata = airportInfo.iata_faa || airportInfo.iata || null
  return airportMatchesFlight(address, flightIata, airportInfo, {
    permissiveWhenFlightIataUnknown: false,
  })
}

// ── Is this flight the one the booking is for? (day + airport) ─────────────────────────
// ONE rule for every surface that decides whether a flight is "verified" for a booking: the
// booking forms (AirportPickupSection / AirportDropOffSection), the admin inline verdict
// (useInlineFlightStatus) and the booking pages (FlightTracking `verifiedFlightsOnly`).
//
// Times are the airport's wall clock — bookings store it written as UTC ("…Z"), flight
// payloads carry it with the airport's offset — and both ends of a comparison are at the SAME
// airport, so their wall clocks can be compared directly.

// A time as { day: 'YYYY-MM-DD', at: wall-clock ms | null }.
//  - Date        → its UTC fields (never the browser's zone — `moment(date, fmt, true)`
//                  ignores the format for a Date and reads it in local time).
//  - 'DD/MM/YYYY' / 'YYYY-MM-DD' → date only (`at` null).
//  - ISO date-time → the wall clock as written (parseZone keeps the given offset).
export function wallClockOf(value) {
  if (!value) return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return { day: value.toISOString().slice(0, 10), at: value.getTime() }
  }
  if (typeof value !== 'string') return null
  const strict = moment(value, 'DD/MM/YYYY', true)
  if (strict.isValid()) return { day: strict.format('YYYY-MM-DD'), at: null }
  const parsed = moment.parseZone(value)
  if (!parsed.isValid()) return null
  const hasTime = /\d[T ]\d{1,2}:\d{2}/.test(value)
  return {
    day: parsed.format('YYYY-MM-DD'),
    at: hasTime
      ? Date.UTC(
          parsed.year(),
          parsed.month(),
          parsed.date(),
          parsed.hours(),
          parsed.minutes()
        )
      : null,
  }
}

// The days differ legitimately only across midnight, and only by a little:
//  arrival   — pick-up shortly AFTER a late landing (lands 23:50, pick-up 00:05 next day,
//              e.g. with the chauffeur arrival buffer). Up to 3h: the same cut-off the flight
//              lookup uses (a pick-up up to 03:00 belongs to the previous day's flight).
//  departure — drop-off shortly BEFORE an after-midnight take-off (car 22:30, flight 01:15).
//              Up to 6h, a normal check-in lead.
const NEXT_DAY_WINDOW_MS = { arrival: 3 * 3600000, departure: 6 * 3600000 }

// true = same day (or within the cross-midnight window), false = a different day,
// null = either side has no usable date (callers decide: the forms do not flag it, the
// booking pages do not show the card).
export function flightTimeMatchesBooking({ flightTime, bookedTime, side }) {
  const flight = wallClockOf(flightTime)
  const booked = wallClockOf(bookedTime)
  if (!flight || !booked) return null
  if (flight.day === booked.day) return true
  if (flight.at == null || booked.at == null) return false
  const gap =
    side === 'departure' ? flight.at - booked.at : booked.at - flight.at
  const window =
    NEXT_DAY_WINDOW_MS[side === 'departure' ? 'departure' : 'arrival']
  return gap >= 0 && gap <= window
}

// Verified = at the booked airport AND on the booked day. `airportInfo` is the flight's own
// airport on the booked side ({ iata_faa | iata, name, city }).
export function isFlightVerifiedForBooking({
  airportInfo,
  flightTime,
  side,
  bookedAirport,
  bookedTime,
}) {
  if (
    !airportMatchesFlight(bookedAirport, airportInfo?.iata_faa, airportInfo)
  ) {
    return false
  }
  return flightTimeMatchesBooking({ flightTime, bookedTime, side }) === true
}
